/**
 * <s-gallery> — product gallery with a single-slide snap track and thumbnails.
 *
 * Required markup:
 *   [data-gallery-track]               — horizontally scrolling snap container.
 *   [data-gallery-slide]               — one child per media item, in order.
 *   button[data-gallery-thumb][data-index] — thumbnail controls (optional).
 *
 * Thumbnails scroll the track to their slide; the visible slide marks its
 * thumbnail with aria-current="true". Scrolling respects reduced motion.
 */
class SGallery extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;

    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (!thumb || !this.contains(thumb)) return;
      this.goTo(Number(thumb.dataset.index));
    }, opts);

    const slides = [...this.track.querySelectorAll('[data-gallery-slide]')];
    if (slides.length < 2 || !('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          this.#markCurrent(slides.indexOf(entry.target));
        }
      }
    }, { root: this.track, threshold: [0.6] });
    slides.forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  goTo(index) {
    const slide = this.track?.querySelectorAll('[data-gallery-slide]')[index];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll only the track: offsetLeft is relative to the track's start edge, which also holds in RTL via the sign flip.
    const isRtl = getComputedStyle(this.track).direction === 'rtl';
    const start = isRtl ? slide.offsetLeft + slide.offsetWidth - this.track.clientWidth : slide.offsetLeft;
    this.track.scrollTo({ left: start, behavior: reduce ? 'auto' : 'smooth' });
    this.#markCurrent(index);
  }

  #markCurrent(index) {
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      if (Number(thumb.dataset.index) === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-gallery')) {
  customElements.define('s-gallery', SGallery);
}
